import React, { useState, useEffect } from 'react';
import { projectsApi, Project, ProjectTarget } from '../../services/projectsApi';
import { COUNTRY_LIST, normalizeCountry } from '../../config/countries';
import type { CountryCode } from '../../config/countries';

interface SavePolygonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, description: string, project?: ProjectTarget, country?: CountryCode) => Promise<void>;
  /** The country could not be told from the shape: ask for it. */
  askCountry?: boolean;
  /** The country the select starts on, such as the one the page is filtered to. */
  defaultCountry?: CountryCode | null;
  hasPolygons: boolean;
  editMode?: boolean;
  initialName?: string;
  initialDescription?: string;
  onSkip?: () => void;
  currentPolygonNumber?: number;
  totalPolygons?: number;
}

const SavePolygonModal: React.FC<SavePolygonModalProps> = ({
  isOpen,
  onClose,
  onSave,
  hasPolygons,
  editMode = false,
  initialName = '',
  initialDescription = '',
  onSkip,
  currentPolygonNumber,
  totalPolygons,
  askCountry = false,
  defaultCountry = null
}) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectChoice, setProjectChoice] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [countryChoice, setCountryChoice] = useState<string>(defaultCountry ?? '');
  const [showCountryError, setShowCountryError] = useState(false);
  const [showNameError, setShowNameError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [savedData, setSavedData] = useState<{ name: string } | null>(null);

  // Update local state when initial values change (for edit mode)
  useEffect(() => {
    setName(initialName);
    setDescription(initialDescription);
  }, [initialName, initialDescription]);

  // Each polygon starts on the default country again.
  useEffect(() => {
    setCountryChoice(defaultCountry ?? '');
    setShowCountryError(false);
  }, [defaultCountry, currentPolygonNumber, isOpen]);

  // Load projects so a new area can be added to one on save.
  useEffect(() => {
    if (isOpen && !editMode) {
      projectsApi.list().then(setProjects).catch(() => {});
    }
  }, [isOpen, editMode]);

  const handleContinue = async () => {
    if (!name.trim()) {
      setShowNameError(true);
      return;
    }

    const country = askCountry ? normalizeCountry(countryChoice) : null;
    if (askCountry && !country) {
      setShowCountryError(true);
      return;
    }

    setIsLoading(true);
    try {
      let project: ProjectTarget | undefined;
      if (!editMode) {
        if (projectChoice === '__new__' && newProjectName.trim()) {
          project = { kind: 'new', name: newProjectName.trim() };
        } else if (projectChoice && projectChoice !== '__new__') {
          project = { kind: 'existing', id: projectChoice };
        }
      }
      await onSave(name, description, project, country ?? undefined);

      setSavedData({ name });
      setShowSuccess(true);

      // Reset form for next polygon
      setName('');
      setDescription('');

      // Auto-close after showing success for 1.5 seconds
      setTimeout(() => {
        setShowSuccess(false);
        setSavedData(null);
      }, 1500);
    } catch (error) {
      console.error('Failed to save polygon:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to save polygon: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    // Reset form and call skip handler
    setName('');
    setDescription('');
    setShowNameError(false);
    if (onSkip) {
      onSkip();
    }
  };

  const handleClose = () => {
    setName(initialName);
    setDescription(initialDescription);
    setShowNameError(false);
    setShowSuccess(false);
    setSavedData(null);
    onClose();
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (showNameError && e.target.value.trim()) {
      setShowNameError(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-lg">
        {!hasPolygons ? (
          <>
            <h2 className="text-lg font-bold mb-4">No Polygon Drawn</h2>
            <p>Please draw at least one polygon on the map before saving.</p>
            <div className="modal-action">
              <button className="btn" onClick={onClose}>Close</button>
            </div>
          </>
        ) : showSuccess ? (
          <div className="text-center py-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-success-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-bold text-success mb-2">{editMode ? 'Polygon Updated!' : 'Polygon Saved!'}</h2>
            <p className="text-base-content/70 mb-2">{savedData?.name}</p>
            <p className="text-xs text-base-content/40 mt-4">Closing automatically...</p>
          </div>
        ) : (
          <>
            {totalPolygons && totalPolygons > 1 && (
              <div className="text-center mb-4 pb-2 border-b border-base-300">
                <p className="text-2xl font-bold text-primary">
                  Saving {currentPolygonNumber} of {totalPolygons} Polygons
                </p>
              </div>
            )}
            <h2 className="text-lg font-bold mb-4">
              {editMode ? 'Edit Polygon' : 'Save Polygon'}
            </h2>

            <div className="form-control w-full mb-4">
              <label className="label">
                <span className="label-text">Name *</span>
              </label>
              <input
                type="text"
                placeholder="Enter polygon name"
                className={`input input-bordered w-full ${showNameError ? 'input-error' : ''}`}
                value={name}
                onChange={handleNameChange}
              />
              {showNameError && (
                <label className="label">
                  <span className="label-text-alt text-error">Please enter a name</span>
                </label>
              )}
            </div>

            <div className="form-control w-full mb-4">
              <label className="label">
                <span className="label-text">Description</span>
              </label>
              <textarea
                className="textarea textarea-bordered h-24"
                placeholder="Enter description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {askCountry && (
              <div className="form-control w-full mb-4">
                <label className="label" htmlFor="save-polygon-country">
                  <span className="label-text">Country *</span>
                </label>
                <select
                  id="save-polygon-country"
                  className={`select select-bordered w-full ${showCountryError ? 'select-error' : ''}`}
                  value={countryChoice}
                  onChange={(e) => {
                    setCountryChoice(e.target.value);
                    setShowCountryError(false);
                  }}
                >
                  <option value="">Choose a country</option>
                  {COUNTRY_LIST.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.label}
                    </option>
                  ))}
                </select>
                <label className="label">
                  <span className={`label-text-alt ${showCountryError ? 'text-error' : ''}`}>
                    {showCountryError
                      ? 'Please choose a country'
                      : "We couldn't tell the country from where this is drawn."}
                  </span>
                </label>
              </div>
            )}

            {!editMode && (
              <div className="form-control w-full mb-4">
                <label className="label">
                  <span className="label-text">Add to project</span>
                </label>
                <select
                  className="select select-bordered w-full"
                  value={projectChoice}
                  onChange={(e) => setProjectChoice(e.target.value)}
                >
                  <option value="">No project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                  <option value="__new__">+ New project…</option>
                </select>
                {projectChoice === '__new__' && (
                  <input
                    type="text"
                    placeholder="New project name"
                    className="input input-bordered w-full mt-2"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                  />
                )}
              </div>
            )}

            <div className="modal-action">
              <button className="btn btn-ghost" onClick={handleClose}>
                Cancel All
              </button>
              {onSkip && totalPolygons && totalPolygons > 1 && (
                <button className="btn btn-outline" onClick={handleSkip}>
                  Skip
                </button>
              )}
              <button
                className={`btn btn-primary ${!name.trim() || isLoading ? 'btn-disabled' : ''}`}
                onClick={handleContinue}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Saving...
                  </>
                ) : (
                  editMode ? 'Update' : 'Save'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SavePolygonModal;
